import type { FantasyContest } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useState } from 'react';
import { useScreenViewed } from '../../api';
import { entryLine } from '../../fantasy/card';
import {
  STATUS_LABEL,
  budgetUnit,
  formatAmount,
  formatLockTime,
  statusOf,
} from '../../fantasy/roster';
import { usePlayer } from '../../player';
import { useFantasyContests } from '../../queries';
import { ContestScreen } from './ContestScreen';

/**
 * §9.2's way in: what this room is running, and whether you are in it.
 *
 * <b>Ordered by what wants attention</b> — open for entries first, then underway, then finished,
 * and drafts never appear at all. A draft is a contest the room has not published; it has no pool,
 * no lock time and nothing anybody can do with it, so listing one would be an invitation to a thing
 * that does not exist yet.
 *
 * Each card answers the three questions somebody has before opening it: can I still enter, what
 * does a team cost me, and when does it close.
 */
export function FantasyView() {
  const { player, room, theme } = usePlayer();
  useScreenViewed('fantasy', player?.roomId ?? null);
  const contestsQ = useFantasyContests(room?.id ?? null);
  const [openId, setOpenId] = useState<string | null>(null);

  const contests = (contestsQ.data ?? []).filter((c) => statusOf(c) !== 'draft');
  const open = openId ? (contests.find((c) => c.id === openId) ?? null) : null;

  if (open) return <ContestScreen contest={open} onBack={() => setOpenId(null)} />;

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  if (contestsQ.isLoading) return <Spinner label="Loading contests…" />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fantasy</h1>
        <p className="mt-1 text-sm" style={{ color: theme.textMuted }}>
          Pick a team of players from your room and score whatever they score.
        </p>
      </div>

      {contests.length === 0 ? (
        <p style={card} className="rounded-xl p-8 text-center text-sm">
          <span style={{ color: theme.textMuted }}>
            No contests are running at your room yet.
          </span>
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {byAttention(contests).map((contest) => (
            <ContestCard key={contest.id} contest={contest} onOpen={() => setOpenId(contest.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function ContestCard({ contest, onOpen }: { contest: FantasyContest; onOpen: () => void }) {
  const { theme, readOnly } = usePlayer();
  const status = statusOf(contest);
  const unit = budgetUnit(contest);
  // The entry you hold — the server's count of your own (#234) — and the one thing on the card in orange.
  const entry = entryLine(contest, status, readOnly);

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{ background: theme.panel, border: `1px solid ${theme.border}` }}
      className="rounded-xl p-5 text-left transition hover:bg-white/5"
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <h2 className="min-w-0 font-semibold">{contest.name}</h2>
        <span
          className="shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={
            // Orange is "this is you" on this side of the glass and never a control, so an open
            // contest is marked in chalk. What earns the accent is the entry you hold, below.
            status === 'open'
              ? { background: `${theme.text}14`, color: theme.text }
              : { background: theme.border, color: theme.textMuted }
          }
        >
          {STATUS_LABEL[status]}
        </span>
      </div>

      {contest.description ? (
        <p className="mb-3 text-xs" style={{ color: theme.textMuted }}>
          {contest.description}
        </p>
      ) : null}

      <div className="space-y-1 text-xs" style={{ color: theme.textMuted }}>
        <div>
          {contest.rosterSize} players · {formatAmount(contest.budget, unit)} to spend
          {contest.captainEnabled ? ` · captain scores ×${contest.captainMultiplier ?? 1}` : ''}
        </div>
        <div>
          {status === 'open'
            ? `Entries close ${formatLockTime(contest.locksAt)}`
            : status === 'locked'
              ? 'Entries are closed — scoring now'
              : 'Final'}
          {' · '}
          {contest.entries === 1 ? '1 entry' : `${contest.entries} entries`}
        </div>
      </div>

      {entry ? (
        <p
          className="mt-3 text-xs font-semibold"
          style={{ color: entry.self ? theme.accent : theme.textMuted }}
        >
          {entry.text}
        </p>
      ) : null}

      {/* §6.2's Hold, on the card as well as the standings: somebody scanning the list for why
          their team has not moved should find the answer here rather than having to open it. */}
      {contest.standingsPaused ? (
        <p className="mt-3 text-[11px]" style={{ color: theme.textMuted }}>
          Scoring is on hold while the room reviews a change to this contest.
        </p>
      ) : null}
    </button>
  );
}

/**
 * Open first, then underway, then finished — the order of how much a player can still do about it.
 *
 * Within a stage, by name. There is no date on the contract to sort on: `FantasyContestDto` carries
 * `publishedAt` and `lockedAt` but nothing that orders two contests at the same stage in a way a
 * reader would predict, and ordering on the key would work today for a reason nobody should rely
 * on (the ids are time-ordered v7 GUIDs, which is how they are minted rather than anything the
 * contract states).
 *
 * Sorted on a copy: the query's array is cached and TanStack hands back the same reference.
 */
function byAttention(contests: readonly FantasyContest[]): FantasyContest[] {
  const order: Record<string, number> = { open: 0, locked: 1, closed: 2, draft: 3 };
  return [...contests].sort(
    (a, b) =>
      (order[statusOf(a)] ?? 9) - (order[statusOf(b)] ?? 9) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}
